import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ZarorHeader from './components/ZarorHeader';
import Footer from './components/Footer';
import SocialMetaUpdater from './components/SocialMetaUpdater';
import Home from './pages/Home';
import FootballDesign from './pages/FootballDesign';
import Tracksuits from './pages/Tracksuits';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import Post from './pages/Post';
import About from './pages/About';
import Contact from './pages/Contact';
import ForgotPassword from './pages/ForgotPassword';
import NotFound from './pages/NotFound';
import TshirtSelection from './pages/TshirtSelection';
import ColorSelection from './pages/ColorSelection';
import LogoCustomization from './pages/LogoCustomization';
import OrderForm from './pages/OrderForm';
import ThankYou from './pages/ThankYou';
import { AuthProvider } from './context/AuthContext';
import FAQs from './pages/FAQs';
import Overview from './pages/Overview';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import { Box, CircularProgress } from '@mui/material';

const BlogList = lazy(() => import('./pages/BlogList'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));

const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
    <CircularProgress />
  </Box>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
      <AuthProvider>
        <SocialMetaUpdater />
        <Router>
          <ScrollToTop />
          <div className="App">
          <Routes>
            <Route path="/" element={
              <>
                <ZarorHeader />
                <main>
                  <Home />
                </main>
                <Footer />
              </>
            } />
              
              {/* T-Shirt Customization Routes */}
              <Route path="/tshirt-selection" element={
                <>
                  <ZarorHeader />
                  <main>
                    <TshirtSelection />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/color-selection" element={
                <>
                  <ZarorHeader />
                  <main>
                    <ColorSelection />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/customize" element={
                <>
                  <ZarorHeader />
                  <main>
                    <LogoCustomization />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/order-form" element={
                <>
                  <ZarorHeader />
                  <main>
                    <OrderForm />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/thank-you" element={
                <>
                  <ZarorHeader />
                  <main>
                    <ThankYou />
                  </main>
                  <Footer />
                </>
              } />

              {/* Authentication Routes */}
              <Route path="/sign-in" element={
                <>
                  <ZarorHeader />
                  <main>
                    <SignIn />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/signin" element={<Navigate to="/sign-in" replace />} />
              <Route path="/login" element={<Navigate to="/sign-in" replace />} />
              <Route path="/sign-up" element={
                <>
                  <ZarorHeader />
                  <main>
                    <SignUp />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/signup" element={<Navigate to="/sign-up" replace />} />
              <Route path="/register" element={<Navigate to="/sign-up" replace />} />
              <Route path="/forgot-password" element={
                <>
                  <ZarorHeader />
                  <main>
                    <ForgotPassword />
                  </main>
                  <Footer />
                </>
              } />

              {/* Other Specific Routes */}
              <Route path="/post/:id" element={
                <>
                  <ZarorHeader />
                  <main>
                    <Post />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/about" element={
                <>
                  <ZarorHeader />
                  <main>
                    <About />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/contact" element={
                <>
                  <ZarorHeader />
                  <main>
                    <Contact />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/faqs" element={
                <>
                  <ZarorHeader />
                  <main>
                    <FAQs />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/overview" element={
                <>
                  <ZarorHeader />
                  <main>
                    <Overview />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/privacy-policy" element={
                <>
                  <ZarorHeader />
                  <main>
                    <PrivacyPolicy />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/terms-of-service" element={
                <>
                  <ZarorHeader />
                  <main>
                    <TermsOfService />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/tracksuits" element={
                <>
                  <ZarorHeader />
                  <main>
                    <Tracksuits />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/blog" element={
                <>
                  <ZarorHeader />
                  <main>
                    <Suspense fallback={<PageLoader />}>
                      <BlogList />
                    </Suspense>
                  </main>
                  <Footer />
                </>
              } />
              <Route path="/blog/:slug" element={
                <>
                  <ZarorHeader />
                  <main>
                    <Suspense fallback={<PageLoader />}>
                      <BlogDetail />
                    </Suspense>
                  </main>
                  <Footer />
                </>
              } />
              
              {/* Wildcard Routes - These should be last */}
              {/* All category routes use FootballDesign for kit preview functionality */}
              <Route path="/:slug" element={
                <>
                  <ZarorHeader />
                  <main>
                    <FootballDesign />
                  </main>
                  <Footer />
                </>
              } />
              <Route path="*" element={
                <>
                  <ZarorHeader />
                  <main>
                    <NotFound />
                  </main>
                  <Footer />
                </>
              } />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
  );
}

export default App;
